/**
 * Restore-from-backup logic, separated from the DataBackup UI component
 * to keep `react-refresh/only-export-components` happy and make the
 * pure-data parts testable.
 *
 * Two main jobs here:
 *
 *   1. **UUID remapping for legacy backups.** v0.6.0 backups used slug
 *      IDs like `debt-robinhood-cc`. The Supabase schema requires
 *      `id uuid primary key`, so pushing those rows up would 400 the
 *      INSERT. When we detect non-UUID IDs we generate fresh UUIDs for
 *      every debt + income source and rewrite the FK references in the
 *      ledger entries (`allocations[].debtId`, `income[].sourceId`).
 *
 *   2. **Cloud push.** Signed-in users have their UI bound to cloud
 *      repos — writing only to localStorage would leave the restored
 *      data invisible. v0.8.14 pushes the restored payload up to the
 *      bound Supabase repos via `bulkReplace`, so cloud + localStorage
 *      end up in sync and the user sees their data immediately after
 *      the post-restore reload.
 */
import type {
  Debt,
  IncomeSource,
  MonthlyLedgerEntry,
  RecurringExpense,
  RecoveryAnchor,
} from '@/types'
import {
  getRepository,
  getLedgerRepository,
  getRecurringExpenseRepository,
  getRecoveryAnchorRepository,
} from '@/repository'
import { safeUUID } from '@/lib/uuid'
import { BACKUP_KEYS } from './dataBackupKeys'

// ─── Backup parsing ──────────────────────────────────────────────────────

/**
 * Structured view of the backup's user-data sections, normalised to the
 * runtime entity types. Missing sections collapse to empty arrays / null
 * so callers don't have to null-check every step.
 */
export interface RestorePayload {
  debts: Debt[]
  incomeSources: IncomeSource[]
  ledger: MonthlyLedgerEntry[]
  recurringExpenses: RecurringExpense[]
  recoveryAnchor: RecoveryAnchor | null
  /**
   * Passthrough — the Zustand-persist blob of dismissed alert ids. Not
   * remapped; just round-tripped from JSON back to localStorage so the
   * user's dismissal history survives a restore. Pre-v0.8.14 the
   * v0.8.13 inlined for-loop round-tripped this implicitly; the typed
   * payload model needs the slot explicit. On legacy backups where
   * debt ids get remapped, individual dismissals can stale-key out
   * (the new alert id won't match) — acceptable UX cost.
   */
  notifications: unknown | null
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : []
}

export function parseBackupKeys(keys: Record<string, unknown>): RestorePayload {
  const anchor = keys['debtos:recoveryAnchor:v1']
  const notifications = keys['debtos:notifications:v1']
  return {
    debts: asArray<Debt>(keys['debtos:debts:v1']),
    incomeSources: asArray<IncomeSource>(keys['debtos:incomeSources:v1']),
    ledger: asArray<MonthlyLedgerEntry>(keys['debtos:ledger:v1']),
    recurringExpenses: asArray<RecurringExpense>(keys['debtos:recurringExpenses:v1']),
    recoveryAnchor:
      typeof anchor === 'object' && anchor !== null ? (anchor as RecoveryAnchor) : null,
    notifications: notifications === undefined ? null : notifications,
  }
}

// ─── UUID detection + remap ──────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUUID(s: unknown): boolean {
  return typeof s === 'string' && UUID_RE.test(s)
}

/**
 * Returns true if any debt or income-source ID in the payload is NOT a
 * valid UUID. Triggers the remap path. Legacy v0.6.0 backups (slug IDs
 * like `debt-robinhood-cc`) all fall into this bucket.
 */
export function needsUUIDRemap(payload: RestorePayload): boolean {
  for (const d of payload.debts) if (!isUUID(d.id)) return true
  for (const s of payload.incomeSources) if (!isUUID(s.id)) return true
  for (const l of payload.ledger) if (!isUUID(l.id)) return true
  for (const r of payload.recurringExpenses) if (!isUUID(r.id)) return true
  return false
}

/**
 * Replaces every non-UUID id in the payload with a freshly minted UUID.
 * FK references on ledger entries — `allocations[].debtId` and
 * `income[].sourceId` — are rewritten to point at the new UUIDs.
 *
 * Idempotent for valid backups (UUIDs stay as-is). Returns a NEW payload
 * object; the input is not mutated, so the caller can keep the original
 * around for diagnostics if needed.
 */
export function remapLegacyIds(payload: RestorePayload): RestorePayload {
  // Notifications are passed through unchanged. Individual dismissals
  // can become stale-keyed if the underlying debt was remapped (the new
  // alert id `promo-{newUuid}-{date}` won't match the dismissed
  // `promo-{oldSlug}-{date}`), which surfaces as previously-dismissed
  // alerts re-appearing. Acceptable — they're UI dismissals, not
  // first-class data.
  const debtIdMap = new Map<string, string>()
  const sourceIdMap = new Map<string, string>()
  const ledgerIdMap = new Map<string, string>()
  const expenseIdMap = new Map<string, string>()

  function mapId(oldId: string, store: Map<string, string>): string {
    if (isUUID(oldId)) return oldId
    let next = store.get(oldId)
    if (!next) {
      next = safeUUID()
      store.set(oldId, next)
    }
    return next
  }

  const remappedDebts = payload.debts.map((d) => ({ ...d, id: mapId(d.id, debtIdMap) }))
  const remappedIncome = payload.incomeSources.map((s) => ({
    ...s,
    id: mapId(s.id, sourceIdMap),
  }))
  const remappedExpenses = payload.recurringExpenses.map((r) => ({
    ...r,
    id: mapId(r.id, expenseIdMap),
  }))

  const remappedLedger = payload.ledger.map((l) => ({
    ...l,
    id: mapId(l.id, ledgerIdMap),
    allocations: l.allocations.map((a) => ({
      ...a,
      debtId: debtIdMap.get(a.debtId) ?? a.debtId,
    })),
    income: l.income.map((i) => ({
      ...i,
      sourceId: sourceIdMap.get(i.sourceId) ?? i.sourceId,
    })),
  }))

  // Recovery anchor doesn't carry FKs we need to remap, and its shape
  // (RecoveryAnchor) currently has no `id` field — the cloud table keys on
  // `workspace_id` (one anchor per workspace, set via the get/set/clear
  // interface, no per-row id). Pass through unchanged.
  const remappedAnchor: RecoveryAnchor | null = payload.recoveryAnchor
    ? { ...payload.recoveryAnchor }
    : null

  return {
    debts: remappedDebts,
    incomeSources: remappedIncome,
    ledger: remappedLedger,
    recurringExpenses: remappedExpenses,
    recoveryAnchor: remappedAnchor,
    notifications: payload.notifications,
  }
}

// ─── Orphan-reference cleanup ────────────────────────────────────────────

/**
 * Counts of references dropped by `cleanOrphanReferences`. Zero on a
 * well-formed payload; non-zero when ledger entries point at debts /
 * income sources that don't exist in the corresponding arrays.
 */
export interface OrphanCleanupReport {
  droppedAllocations: number
  droppedIncomeEntries: number
}

/**
 * Drops ledger child rows that reference entities not present in the
 * payload. Local-mode tolerates orphan references (no FK constraints on
 * localStorage), but the cloud schema enforces:
 *
 *   - `monthly_income_entries.source_id → income_sources.id`
 *   - `debt_allocations.debt_id → debts.id`
 *
 * A v0.8.14 restore push died on `monthly_income_entries_source_id_fkey`
 * when a historical ledger entry referenced an income source the user
 * had deleted at some point. The orphan reference was already broken
 * data — whatever month it lived on would render with a missing source
 * — so cleaning it is consistent with cloud invariants and doesn't lose
 * meaningful information.
 *
 * Returns a NEW payload (input unchanged) plus a count of what was
 * dropped so the caller can surface a "Dropped N references" note.
 * Idempotent for clean payloads.
 */
export function cleanOrphanReferences(payload: RestorePayload): {
  payload: RestorePayload
  report: OrphanCleanupReport
} {
  const validDebtIds = new Set(payload.debts.map((d) => d.id))
  const validSourceIds = new Set(payload.incomeSources.map((s) => s.id))

  let droppedAllocations = 0
  let droppedIncomeEntries = 0

  const cleanedLedger = payload.ledger.map((entry) => {
    const keptAllocations = entry.allocations.filter((a) => {
      const keep = validDebtIds.has(a.debtId)
      if (!keep) droppedAllocations++
      return keep
    })
    const keptIncome = entry.income.filter((i) => {
      const keep = validSourceIds.has(i.sourceId)
      if (!keep) droppedIncomeEntries++
      return keep
    })

    // Allocate fresh objects only when something actually changed —
    // keeps the structural-equality identity for clean entries so
    // downstream React renders aren't disrupted unnecessarily.
    if (
      keptAllocations.length === entry.allocations.length &&
      keptIncome.length === entry.income.length
    ) {
      return entry
    }
    return { ...entry, allocations: keptAllocations, income: keptIncome }
  })

  return {
    payload: { ...payload, ledger: cleanedLedger },
    report: { droppedAllocations, droppedIncomeEntries },
  }
}

// ─── localStorage write ──────────────────────────────────────────────────

/**
 * Writes the (possibly remapped) payload to the canonical `debtos:*` keys.
 * Clears any pre-existing user-data keys first so a stale value doesn't
 * survive the restore.
 */
export function writePayloadToLocalStorage(payload: RestorePayload): void {
  for (const key of BACKUP_KEYS) {
    localStorage.removeItem(key)
  }
  localStorage.setItem('debtos:debts:v1', JSON.stringify(payload.debts))
  localStorage.setItem('debtos:incomeSources:v1', JSON.stringify(payload.incomeSources))
  localStorage.setItem('debtos:ledger:v1', JSON.stringify(payload.ledger))
  localStorage.setItem('debtos:recurringExpenses:v1', JSON.stringify(payload.recurringExpenses))
  if (payload.recoveryAnchor) {
    localStorage.setItem('debtos:recoveryAnchor:v1', JSON.stringify(payload.recoveryAnchor))
  }
  if (payload.notifications !== null && payload.notifications !== undefined) {
    localStorage.setItem('debtos:notifications:v1', JSON.stringify(payload.notifications))
  }
}

// ─── Cloud push ──────────────────────────────────────────────────────────

/**
 * Section identifiers attached to errors thrown by `pushPayloadToBoundRepos`
 * so the DataBackup UI can render which step failed instead of a generic
 * "restore failed" — meaningful for diagnosing RLS denials vs FK errors vs
 * network drops on a specific row family.
 */
export type PushSection =
  | 'debts'
  | 'incomeSources'
  | 'ledger'
  | 'recurringExpenses'
  | 'recoveryAnchor'

/**
 * Error subclass enriched with the section that failed. `instanceof` works
 * cleanly; callers can also check `err.section` directly.
 *
 * Re-pushing the same payload after a section failure is safe: every
 * cloud bulkReplace is idempotent (delete-then-insert under the same
 * workspace_id). Sections that already succeeded re-overwrite to the
 * same state; the previously-failing section gets another attempt.
 */
export class PushSectionError extends Error {
  readonly section: PushSection
  readonly cause: unknown
  constructor(section: PushSection, cause: unknown) {
    const message = cause instanceof Error ? cause.message : String(cause)
    super(`[${section}] ${message}`)
    this.name = 'PushSectionError'
    this.section = section
    this.cause = cause
  }
}

/**
 * Pushes the payload up to whichever repositories are currently bound.
 * For signed-in users that's the Supabase cloud repos via
 * `bindReposToSession`; for anonymous users this is a no-op write to the
 * local-storage repos (harmless — we already wrote localStorage above).
 *
 * Order matters for FK sanity: debts + income before ledger (which
 * references both), then expenses + anchor as standalone tail.
 *
 * **Partial-failure semantics (v0.8.24).** If section N fails, sections
 * 1..N-1 are already persisted to cloud while N..end are not. localStorage
 * is also already ahead — `writePayloadToLocalStorage` runs before this
 * function. The caller surfaces the section-tagged error to the user with
 * a retry button; re-pushing the same payload is idempotent (`bulkReplace`
 * delete-then-insert) so the partially-pushed state converges cleanly on
 * retry. This beats the alternatives (cloud-first risks "local has stale
 * after cloud has new"; snapshot+restore adds a multi-section rollback
 * surface that's its own correctness problem).
 */
export async function pushPayloadToBoundRepos(payload: RestorePayload): Promise<void> {
  await pushSection('debts', () => getRepository().bulkReplace(payload.debts))
  const ledgerRepo = getLedgerRepository()
  await pushSection('incomeSources', () =>
    ledgerRepo.bulkReplaceIncomeSources(payload.incomeSources)
  )
  await pushSection('ledger', () => ledgerRepo.bulkReplaceLedgerEntries(payload.ledger))
  await pushSection('recurringExpenses', () =>
    getRecurringExpenseRepository().bulkReplace(payload.recurringExpenses)
  )
  await pushSection('recoveryAnchor', () =>
    payload.recoveryAnchor
      ? getRecoveryAnchorRepository().set(payload.recoveryAnchor)
      : getRecoveryAnchorRepository().clear()
  )
}

async function pushSection(section: PushSection, fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn()
  } catch (err) {
    throw new PushSectionError(section, err)
  }
}
